import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, CheckCircle2, Phone, Award, ShieldCheck } from 'lucide-react';
import { mockProspects } from '../../shared/data/prospects';
import { OnboardingProposalModal } from '../../components/proposal/OnboardingProposalModal';
import { HeroSection } from '../../components/landing/HeroSection';
import { TrustedBySection } from '../../components/landing/TrustedBySection';
import { GenesisBentoGrid } from '../../components/landing/GenesisBentoGrid';
import { GrowthMachineSection } from '../../components/landing/GrowthMachineSection';

export const PublicHomePage: React.FC = () => {
  const [isBriefingModalOpen, setIsBriefingModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans selection:bg-[#0061FF] selection:text-white">
      
      {/* TOP ANNOUNCEMENT BAR */}
      <div className="bg-slate-950 text-white text-[11px] font-bold uppercase tracking-widest py-2.5 px-6 text-center flex items-center justify-center gap-2 border-b border-slate-800">
         <Sparkles className="w-4 h-4 text-amber-400 fill-amber-400" />
         <span>YouMachine 360° — Plataforma Integrada SaaS Enterprise de Vendas e Presença Digital</span>
      </div>

      {/* HEADER ENTERPRISE (GOOGLE / STRIPE STYLE) */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between gap-4">
          
          {/* Logo */}
          <div className="flex items-center gap-3 shrink-0">
             <div className="w-10 h-10 rounded-2xl bg-slate-950 text-white flex items-center justify-center font-black text-xl shadow-xs">
                Y
             </div>
             <div>
                <h1 className="font-heading font-black text-lg text-slate-900 tracking-tight uppercase leading-none">YOUMACHINE</h1>
                <span className="text-[10px] text-[#0061FF] font-bold uppercase tracking-widest block mt-0.5">Genesis 360°</span>
             </div>
          </div>

          {/* Navigation Links */}
          <nav className="hidden lg:flex items-center gap-6 text-xs font-bold uppercase tracking-wider text-slate-600 shrink-0">
             <a href="#genesis-360" className="hover:text-[#0061FF] transition-colors whitespace-nowrap">Genesis 360°</a>
             <a href="#growth-machine" className="hover:text-[#0061FF] transition-colors whitespace-nowrap">Growth Machine</a>
             <a href="#exemplos" className="hover:text-[#0061FF] transition-colors whitespace-nowrap">Exemplos por Nicho</a>
             <a href="#precos" className="hover:text-[#0061FF] transition-colors whitespace-nowrap">Planos & Preços</a>
          </nav>

          {/* Action CTAs */}
          <div className="flex items-center gap-3 shrink-0">
             <button 
                onClick={() => setIsBriefingModalOpen(true)}
                className="bg-[#0061FF] text-white px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-blue-700 transition-colors shadow-xs flex items-center gap-1.5 whitespace-nowrap"
             >
                <Sparkles className="w-3.5 h-3.5" />
                <span>Gerar Proposta IA</span>
             </button>
             
             <a 
                href="https://wa.me/554988416296?text=Ol%C3%A1!%20Gostaria%20de%20saber%20mais%20sobre%20o%20Ecossistema%20YouMachine" 
                target="_blank" 
                rel="noreferrer"
                className="hidden sm:flex bg-slate-100 text-slate-800 px-3.5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-slate-200 transition-colors border border-slate-200 items-center gap-1.5 whitespace-nowrap"
             >
                <Phone className="w-3.5 h-3.5 text-emerald-600" />
                <span>Consultor</span>
             </a>
          </div>

        </div>
      </header>

      {/* MAIN STORYTELLING FLOW */}
      <main>
         
         {/* 1. HERO SECTION ENTERPRISE */}
         <HeroSection onOpenBriefing={() => setIsBriefingModalOpen(true)} />

         {/* 2. TRUSTED BY LOGOS */}
         <TrustedBySection />

         {/* 3. CORE ECOSYSTEM: GENESIS 360 (BENTO GRID) */}
         <GenesisBentoGrid />

         {/* 4. FLAGSHIP UPGRADE: GROWTH MACHINE (DARK MODE FLUID TRANSITION) */}
         <GrowthMachineSection />

         {/* 5. NICHE DEMOS & EXAMPLES (BENTO TILES) */}
         <section id="exemplos" className="py-24 px-4 md:px-12 bg-slate-50 text-slate-900 font-sans border-t border-slate-200/80">
            <div className="max-w-7xl mx-auto space-y-12 text-left">
               <div className="text-center max-w-3xl mx-auto space-y-4">
                  <span className="text-xs font-bold uppercase tracking-widest text-[#0061FF]">
                     ADAPTAÇÃO POR NICHO DE MERCADO
                  </span>
                  <h2 className="text-3xl md:text-5xl font-heading font-black text-slate-900">
                     Soluções especializadas para cada segmento
                  </h2>
                  <p className="text-slate-600 text-base">
                     Acesse os demonstrativos ao vivo construídos sob medida para os principais nichos de mercado da região.
                  </p>
               </div>

               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {Object.values(mockProspects).map((prospect) => (
                     <div key={prospect.id} className="bg-white border border-slate-200/80 rounded-[24px] p-6 space-y-4 hover:border-[#0061FF]/40 transition-all flex flex-col justify-between">
                        <div className="space-y-3">
                           <div className="w-10 h-10 rounded-2xl bg-blue-50 text-[#0061FF] font-black flex items-center justify-center text-sm border border-blue-100">
                              {prospect.name.charAt(0)}
                           </div>
                           <div>
                              <h3 className="font-bold text-slate-900 text-lg">{prospect.name}</h3>
                              <span className="text-xs text-slate-500 font-medium">{prospect.segment}</span>
                           </div>
                           <p className="text-xs text-slate-600 leading-relaxed">
                              {prospect.tagline || `Solução digital completa de alta conversão para ${prospect.segment}.`}
                           </p>
                        </div>
                        <div className="pt-2 border-t border-slate-100 flex gap-2">
                           <Link 
                              to={`/${prospect.slug}`}
                              className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-900 text-center font-bold text-xs py-2.5 rounded-xl border border-slate-200 transition-colors"
                           >
                              Ver Demo
                           </Link>
                           <Link 
                              to={`/${prospect.slug}/proposta-canvas`}
                              className="flex-1 bg-[#0061FF] hover:bg-blue-700 text-white text-center font-bold text-xs py-2.5 rounded-xl transition-colors"
                           >
                              Proposta IA
                           </Link>
                        </div>
                     </div>
                  ))}
               </div>
            </div>
         </section>

         {/* 6. PRICING & PACKAGES SECTION */}
         <section id="precos" className="py-24 px-4 md:px-12 bg-white text-slate-900 font-sans border-t border-slate-200/80">
            <div className="max-w-7xl mx-auto space-y-12">
               <div className="text-center max-w-3xl mx-auto space-y-4">
                  <span className="text-xs font-bold uppercase tracking-widest text-[#0061FF]">
                     TRANSPARÊNCIA EM INVESTIMENTO
                  </span>
                  <h2 className="text-3xl md:text-5xl font-heading font-black text-slate-900">
                     Planos estruturados para o seu momento
                  </h2>
               </div>

               <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                  
                  {/* STARTER PACKAGE: DO OFF AO ON */}
                  <div className="bg-slate-50 border border-slate-200 rounded-[24px] p-8 space-y-6 text-left flex flex-col justify-between">
                     <div className="space-y-4">
                        <span className="text-xs font-bold uppercase text-slate-500 tracking-wider">PLANO ESSENCIAL</span>
                        <h3 className="text-2xl font-black text-slate-900">Do Off Ao On</h3>
                        <div className="flex items-baseline gap-1">
                           <span className="text-3xl font-black text-slate-900">R$ 990,00</span>
                           <span className="text-xs text-slate-500 font-bold">/mês nos primeiros 6 meses</span>
                        </div>
                        <span className="text-[11px] text-slate-400 block font-medium">(Após 6 meses: R$ 1.990,00/mês)</span>
                        <ul className="space-y-2 text-xs text-slate-600 pt-2">
                           <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Website / PWA App Otimizado</li>
                           <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Linkwi Bio Link 2.0</li>
                           <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Atualização da Ficha GMN</li>
                           <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Suporte Técnico Inclusivo</li>
                        </ul>
                     </div>
                     <button 
                        onClick={() => setIsBriefingModalOpen(true)}
                        className="w-full bg-slate-900 text-white font-bold text-xs py-3.5 rounded-xl hover:bg-slate-800 transition-colors"
                     >
                        Selecionar Plano Essencial
                     </button>
                  </div>

                  {/* PREMIUM PACKAGE: GROWTH MACHINE */}
                  <div className="bg-[#121212] text-white border border-neutral-800 rounded-[24px] p-8 space-y-6 text-left flex flex-col justify-between relative overflow-hidden">
                     <div className="absolute top-4 right-4 bg-[#FFD700] text-slate-950 font-black text-[10px] uppercase px-3 py-1 rounded-full">
                        FLAGSHIP PREMIUM
                     </div>
                     <div className="space-y-4">
                        <span className="text-xs font-bold uppercase text-[#FFD700] tracking-wider">DOMINÂNCIA TOTAL</span>
                        <h3 className="text-2xl font-black text-white">Growth MACHINE</h3>
                        <div className="flex items-baseline gap-1">
                           <span className="text-3xl font-black text-[#FFD700]">R$ 1.990,00</span>
                           <span className="text-xs text-neutral-400 font-bold">/mês no plano anual</span>
                        </div>
                        <ul className="space-y-2 text-xs text-slate-300 pt-2">
                           <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#FFD700]" /> Tudo do Plano Essencial</li>
                           <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#FFD700]" /> Gestão de Tráfego Pago (Google Ads + Meta Ads)</li>
                           <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#FFD700]" /> Integração com Marketplaces (Hub Central)</li>
                           <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#FFD700]" /> Relatórios Mensais de ROAS & Atração 24/7</li>
                        </ul>
                     </div>
                     <button 
                        onClick={() => setIsBriefingModalOpen(true)}
                        className="w-full bg-[#FFD700] text-slate-950 font-black text-xs py-3.5 rounded-xl hover:bg-amber-400 transition-colors"
                     >
                        Contratar Growth Machine Premium
                     </button>
                  </div>

               </div>
            </div>
         </section>

      </main>

      {/* ENTERPRISE FOOTER */}
      <footer className="bg-slate-950 text-white border-t border-slate-800 py-16 px-6">
         <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8 text-left">
            <div className="space-y-3 md:col-span-2">
               <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-xl bg-white text-slate-950 font-black flex items-center justify-center text-sm">
                     Y
                  </div>
                  <span className="font-heading font-black text-lg text-white">YOUMACHINE</span>
               </div>
               <p className="text-slate-400 text-xs max-w-sm font-light">
                  A plataforma SaaS definitiva para unificar vendas, aplicativos PWA, presença no Google e tráfego pago para empresas em expansão.
               </p>
            </div>
            
            <div className="space-y-2">
               <strong className="text-xs font-bold text-slate-300 uppercase tracking-wider block">Soluções</strong>
               <ul className="space-y-1 text-xs text-slate-400">
                  <li><a href="#genesis-360" className="hover:text-white transition-colors">Genesis 360° Engine</a></li>
                  <li><a href="#growth-machine" className="hover:text-white transition-colors">Growth Machine Premium</a></li>
                  <li><a href="#exemplos" className="hover:text-white transition-colors">Demos por Nicho</a></li>
               </ul>
            </div>

            <div className="space-y-2">
               <strong className="text-xs font-bold text-slate-300 uppercase tracking-wider block">Contato</strong>
               <p className="text-xs text-slate-400">Chapecó/SC • Brasil</p>
               <span className="text-xs text-blue-400 font-bold block">suporte@youmachine.com.br</span>
            </div>
         </div>

         <div className="max-w-7xl mx-auto pt-12 border-t border-slate-900 text-center text-[10px] text-slate-500">
            © {new Date().getFullYear()} YouMachine Tecnologia. Todos os direitos reservados.
         </div>
      </footer>

      {/* AUTOCONSTRUTOR DE PROPOSTAS IA MODAL */}
      <OnboardingProposalModal 
         isOpen={isBriefingModalOpen} 
         onClose={() => setIsBriefingModalOpen(false)} 
      />

    </div>
  );
};
